import { Dispatch, SetStateAction, useContext, useState } from "react";
import { getOrganisationsForDiscussion } from "src/service/query";

import { DiscussionVisibilityConstants, OrganisationTypeConstants } from "../../../../../../constants";
import { createDiscussionThread } from "../../../../../../service/interaction";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import { useActivityDashboardContextSwitcher } from "../../../../utils/activities/useActivityDashboardContextSwitcher";
import { ActivityDiscussionContext } from "../../ActivityDiscussionContext";

interface UseNewDiscussionModalProps {
  closeModal: () => void;
  internalDiscussion?: boolean;
  setInternalDiscussion: Dispatch<SetStateAction<boolean | undefined>>;
}

interface UseNewDiscussionModalReturnData {
  message: string;
  setMessage: Dispatch<SetStateAction<string>>;
  externalUserType: string;
  errors: ServiceError[] | undefined;
  handleCloseModal: () => void;
  handleCreateThread: () => Promise<void>;
  isHandleCreateThreadLoading: boolean;
}

export const useNewDiscussionModal = ({
  closeModal,
  internalDiscussion,
  setInternalDiscussion,
}: UseNewDiscussionModalProps): UseNewDiscussionModalReturnData => {
  const { currentOrganisationUuid, currentUserType } = useAuth();
  const { activityUuid, activityDetails, refreshResolvedDiscussionsKPI } = useActivityDashboardContextSwitcher();
  const [message, setMessage] = useState<string>("");
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleCreateThreadLoading, setIsHandleCreateThreadLoading] = useState(false);

  const externalUserType =
    currentUserType === OrganisationTypeConstants.DEVELOPER
      ? OrganisationTypeConstants.VERIFIER
      : OrganisationTypeConstants.DEVELOPER;

  const { refreshDiscussionTables } = useContext(ActivityDiscussionContext);

  const handleCloseModal = (): void => {
    setMessage("");
    setErrors(undefined);
    setInternalDiscussion(undefined);
    closeModal();
  };

  const handleCreateThread = useIsLoadingWrapper(async (): Promise<void> => {
    const orgsRes = await getOrganisationsForDiscussion({
      objectType: activityDetails?.groupUuid ? "Group" : "Project",
      objectUuid: activityDetails?.groupUuid ?? activityDetails?.project.uuid ?? "",
    });
    const targetOrganisationUuids =
      orgsRes.status === Status.Success && orgsRes.data
        ? orgsRes.data
            .filter((o) =>
              internalDiscussion
                ? o.type === currentUserType && o.uuid !== currentOrganisationUuid
                : o.type === externalUserType || (o.type === currentUserType && o.uuid !== currentOrganisationUuid)
            )
            .map((o) => o.uuid)
        : null;

    const res = await createDiscussionThread({
      componentKey: null,
      dataPath: null,
      messageContent: message,
      messageObjectType: "Activity",
      messageObjectUuid: activityUuid ?? "",
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      messageSourceOrganisationUuid: currentOrganisationUuid!,
      messageType: "Comment",
      messageStatus: "Published",
      threadObjectType: "Activity",
      threadObjectUuid: activityUuid ?? "",
      threadType: "Discussion",
      visibility: internalDiscussion ? DiscussionVisibilityConstants.INTERNAL : DiscussionVisibilityConstants.EXTERNAL,
      messageTargetOrganisationUuids: targetOrganisationUuids,
    });

    if (res.status === Status.Success) {
      Toast.success({ message: "Discussion created" });
      refreshDiscussionTables();

      refreshResolvedDiscussionsKPI();
      handleCloseModal();
    }

    if (res.status === Status.Error && res.errors) {
      setErrors(res.errors);
    }
  }, setIsHandleCreateThreadLoading);

  return {
    message,
    setMessage,
    externalUserType,
    errors,
    handleCloseModal,
    handleCreateThread,
    isHandleCreateThreadLoading,
  };
};
